import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'
import { cloneDeep } from 'lodash-es'
import { PrimeTemplate, SelectItem } from 'primeng/api'
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete'
import { Button } from 'primeng/button'
import { Checkbox } from 'primeng/checkbox'
import { Fluid } from 'primeng/fluid'
import { InputNumber } from 'primeng/inputnumber'
import { InputText } from 'primeng/inputtext'
import { Select } from 'primeng/select'
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  map,
  Observable,
  startWith,
  takeUntil,
} from 'rxjs'

import { BillingMode, CountryCodes, PricingPlans, Tsp } from '@ti/contracts'
import { DateRange, PreDefinedRange } from '@ti/core/common/contracts'
import { Memoize } from '@ti/core/common/decorators'
import { getDateRangeFromPredefined, injectDestroy$ } from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import { CheckIconComponent } from '@ti/core/icons/check'
import { CloseIconComponent } from '@ti/core/icons/close'
import { LaunchIconComponent } from '@ti/core/icons/launch'
import { countryValidator, LanguageService } from '@ti/core/ui-kit/i18n'
import { PhoneNumberInputV2Component } from '@ti/core/ui-kit/inputs'
import { DateRangePickerDialogComponent } from '@ti/core/ui-kit/layout/components/date-range-picker-dialog'
import { AutocompleteSelectComponent } from '@ti/core/ui-kit/layout/components/inputs/autocomplete-select'

import { BillingModesProvider } from '../../models/billing-modes.provider'
import { CountyOptionsProvider } from '../../models/country-options.provider'

const DEFAULT_DATA_HISTORY_DAYS = 3 * 31 // 3 Months

@Component({
  selector: 'app-ti-account-form',
  templateUrl: 'account-form.component.html',
  styleUrl: 'account-form.component.scss',
  imports: [
    ReactiveFormsModule,
    Fluid,
    InputText,
    TranslatePipe,
    PhoneNumberInputV2Component,
    AutocompleteSelectComponent,
    AsyncPipe,
    Select,
    PrimeTemplate,
    DateRangePickerDialogComponent,
    Button,
    CloseIconComponent,
    Checkbox,
    InputNumber,
    LaunchIconComponent,
    CheckIconComponent,
  ],
})
export class AccountFormComponent implements OnInit, OnChanges {
  @Output() cancelEvent = new EventEmitter<void>()
  @Output() submitEvent = new EventEmitter()
  @Input() processing = false
  @Input() errorMessage = ''
  @ViewChild('errorBlock') errorBlock!: ElementRef
  @ViewChild('phoneNumberInput') phoneNumberInputRef!: PhoneNumberInputV2Component

  protected BillingMode = BillingMode
  protected readonly DEFAULT_DATA_HISTORY_MONTH = DEFAULT_DATA_HISTORY_DAYS
  protected readonly config = inject(CONFIG)
  protected readonly languageService = inject(LanguageService)
  protected readonly changeDetector = inject(ChangeDetectorRef)
  protected readonly destroy$ = injectDestroy$()
  protected readonly billingModesProvider = inject(BillingModesProvider)
  protected readonly countyOptionsProvider = inject(CountyOptionsProvider)
  protected readonly mediaStorageOptions: { value: number; name: string }[] = []

  protected isLoading = false
  protected modelWasChanged = false
  protected disableMode$ = new BehaviorSubject<boolean>(false)

  protected readonly countrySearch$ = new BehaviorSubject<string | null>('')
  protected readonly currentBillingMode$ = new BehaviorSubject<BillingMode>(BillingMode.Monthly)

  protected readonly TYPES = { CountryCodes }

  protected readonly form = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
    legalName: new FormControl<string>('', [Validators.required]),
    contactName: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    phone: new FormControl<string>(''),
    country: new FormControl<string>('', [Validators.required, countryValidator]),
    address: new FormControl<string>(''),
    city: new FormControl<string>(''),
    region: new FormControl<string>(''),
    zipCode: new FormControl<string>(''),
    usernamePrefix: new FormControl<string | null>(null),
    billingMode: new FormControl<BillingMode>(BillingMode.Monthly),
    billingStartDate: new FormControl<Date | null>(null),
    /// BillingConfig
    tntDevicePrice: new FormControl<number>(0.9),
    standardDevicePrice: new FormControl<number>(1.4),
    advancedDevicePrice: new FormControl<number>(2.7),
    premiumDevicePrice: new FormControl<number>(3.5),

    standardMediaDevicePrice: new FormControl<number>(1.9),
    advancedMediaDevicePrice: new FormControl<number>(3.2),
    premiumMediaDevicePrice: new FormControl<number>(4),

    minimumMonthlyFee: new FormControl<number | null>(200, [
      Validators.min(0),
      Validators.pattern(/^\d+$/), // only whole digits
    ]),
    extraTelemetryPrice: new FormControl<number>(0.5),
    extraStoragePrice: new FormControl<number>(0.5),
    telemetryHistory: new FormControl<number>(DEFAULT_DATA_HISTORY_DAYS),
    storageSize: new FormControl<number>(1),
    // Pricing Plans
    tntPlan: new FormControl<boolean>(true),
    standardPlan: new FormControl<boolean>(true),
    advancedPlan: new FormControl<boolean>(false),
    premiumPlan: new FormControl<boolean>(false),
  })

  protected _account: Partial<Tsp> = {}

  get account(): Partial<Tsp> {
    return this._account
  }

  @Input()
  set account(value: Partial<Tsp> | null | undefined) {
    this._account = value ?? {}
    // set start values

    const billingMode = this.account.billingMode ?? BillingMode.Monthly

    this.form.setValue({
      name: this.account.name || '',
      legalName: this.account.legalName || '',
      contactName: this.account.contactName || '',
      email: this.account.email || '',
      phone: this.account.phone || '',
      country: this.account.country || '',
      address: this.account.address || '',
      city: this.account.city || '',
      region: this.account.region || '',
      zipCode: this.account.zipCode || '',
      usernamePrefix: this.account.usernamePrefix || null,
      billingMode,
      billingStartDate: this.account.billingStartDate || null,
      // BillingConfig
      minimumMonthlyFee: this.account?.billingConfig?.minimumMonthlyFee || null,
      tntDevicePrice: this.account?.billingConfig?.rates?.tntDevicePrice || 0.9,
      standardDevicePrice: this.account?.billingConfig?.rates?.standardDevicePrice || 1.4,
      advancedDevicePrice: this.account?.billingConfig?.rates?.advancedDevicePrice || 2.7,
      premiumDevicePrice: this.account?.billingConfig?.rates?.premiumDevicePrice || 3.4,

      standardMediaDevicePrice: this.account?.billingConfig?.rates?.standardMediaDevicePrice || 1.9,
      advancedMediaDevicePrice: this.account?.billingConfig?.rates?.advancedMediaDevicePrice || 3.2,
      premiumMediaDevicePrice: this.account?.billingConfig?.rates?.premiumMediaDevicePrice || 3.9,

      extraTelemetryPrice: this.account?.billingConfig?.rates?.extraTelemetryPrice || 0.5,
      extraStoragePrice: this.account?.billingConfig?.rates?.extraStoragePrice || 0.5,
      telemetryHistory:
        this.account?.billingConfig?.limits?.telemetryHistory || DEFAULT_DATA_HISTORY_DAYS,
      storageSize: this.account?.billingConfig?.limits?.storageSize || 1,
      // Pricing Plans
      tntPlan: this.account?.billingConfig?.plans[PricingPlans.TnT] ?? true,
      standardPlan: this.account?.billingConfig?.plans[PricingPlans.Standard] ?? true,
      advancedPlan: this.account?.billingConfig?.plans[PricingPlans.Advanced] ?? true,
      premiumPlan: this.account?.billingConfig?.plans[PricingPlans.Premium] ?? true,
    })

    this.currentBillingMode$.next(billingMode)
    this.modelWasChanged = false
    // Roll back once tested and we'd freeze editing prefixes
    // if (!this.isInCreateMode) {
    //   this.form.get('usernamePrefix')?.disable()
    // }
  }

  get isInCreateMode() {
    return !this._account.id
  }

  get isValidForm() {
    return this.form.valid
  }

  @Memoize()
  get countryOptions$(): Observable<SelectItem[]> {
    return combineLatest([this.countyOptionsProvider.countryOptions$, this.countrySearch$]).pipe(
      takeUntil(this.destroy$),
      map(([options, search]) => {
        let data = cloneDeep(options)
        if (search) {
          data = data.filter((value) => value.label?.toLowerCase()?.includes(search.toLowerCase()))
        }
        return data
      }),
    )
  }

  @Memoize()
  get dataHistoryOptions$() {
    return combineLatest([
      this.languageService.massTranslate$({
        months: 'ti-admin.accounts.data-history.months',
        year: 'ti-admin.accounts.data-history.year',
        years: 'ti-admin.accounts.data-history.years',
      }),
      this.form.valueChanges.pipe(startWith(null)),
    ]).pipe(
      debounceTime(256),
      takeUntil(this.destroy$),
      map(([labels]) => {
        const maxYears = 10
        const defaultDataHistoryOption = {
          value: DEFAULT_DATA_HISTORY_DAYS,
          label: `${Math.floor(DEFAULT_DATA_HISTORY_DAYS / 31)} ${labels.months}`,
        }

        const dataHistoryOptions = [defaultDataHistoryOption]
        for (let i = 1; i <= maxYears; i++) {
          dataHistoryOptions.push({
            value: i * 365,
            label: i > 1 ? `${i} ${labels.years}` : `${i} ${labels.year}`,
          })
        }
        return dataHistoryOptions
      }),
    )
  }

  @Memoize()
  get billingModeOptions$(): Observable<SelectItem[]> {
    return combineLatest([this.billingModesProvider.labels$, this.currentBillingMode$]).pipe(
      takeUntil(this.destroy$),
      map(([labels]) => {
        return [
          { label: labels[BillingMode.Monthly], value: BillingMode.Monthly },
          { label: labels[BillingMode.Annual], value: BillingMode.Annual },
        ]
      }),
    )
  }

  async ngOnInit() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.errorMessage = ''
      this.modelWasChanged = true
    })

    this.form
      .get('country')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.phoneNumberInputRef.countryCode = value as CountryCodes
      })

    for (let i = 1; i <= 10; i++) {
      this.mediaStorageOptions.push({ value: i, name: `${i} GB` })
    }

    // Auto-generate on name changes (create mode only)
    if (this.isInCreateMode) {
      this.form
        .get('name')
        ?.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$))
        .subscribe(async (name) => {
          const prefix = this.generatePrefixFromName(name ?? '')
          this.form.get('usernamePrefix')?.setValue(prefix || null, { emitEvent: false })
        })
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['errorMessage'] && this.errorMessage) {
      this.changeDetector.detectChanges()
      this.focusOnErrorDiv()
    }
  }

  public onCancel() {
    if (!this.disableMode$.getValue()) {
      this.form.reset()
      this.cancelEvent.emit()
    }
  }

  protected removeBillingStartDate() {
    this.form.patchValue({
      billingStartDate: null,
    })
  }

  protected onCountrySearch(event: AutoCompleteCompleteEvent) {
    const query = event.query
    this.countrySearch$.next(query ?? '')
  }

  protected async onSubmit() {
    if (!this.form.valid) {
      // this.errorMessage = await this.languageService.translate('common.please-check-entered-data')
      return this.form.markAllAsTouched()
    }
    this.submitEvent.emit(this.form.value)
  }

  protected updateBillingStartDate(dateRange: DateRange | PreDefinedRange | undefined) {
    if (Array.isArray(dateRange)) {
      this.form.patchValue({ billingStartDate: dateRange[0] })
    } else if (typeof dateRange === 'string') {
      this.form.patchValue(
        { billingStartDate: getDateRangeFromPredefined(dateRange)?.[0] },
        { emitEvent: false },
      )
    }
  }

  private generatePrefixFromName(raw: string): string {
    if (!raw) return ''
    // normalize + strip diacritics
    const normalized = raw
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '') // diacritics
      .replace(/ß/g, 'ss')

    // split by space/underscore/dash OR camelCase boundary
    const tokens = normalized.split(/[\s_\-]+|(?<=[a-z])(?=[A-Z])/g).filter(Boolean)

    if (tokens.length === 0) return ''

    // pick first token; if too short, append next
    let base = tokens[0]
    if (base.length < 3 && tokens[1]) base += tokens[1]

    // keep only a-z0-9, lowercase, and clamp length 3..12
    const cleaned = base.toLowerCase().replace(/[^a-z0-9]/g, '')
    return cleaned.slice(0, 5)
  }

  private focusOnErrorDiv(): void {
    if (this.errorBlock) {
      this.errorBlock.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }
}
